import React, { useCallback, useEffect, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'
import { CUSTOMER_PAYMENT_METHODS, GET_ORDER_STATUS } from '@/services/NftService'
import { CollectibleProductResponse } from '@/services/NftService/types'
import { getStripe } from '@/services/StripeService/StripePaymentService'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import NewCardPaymentForm from '../NewCardPaymentForm'
import SavedPaymentMethods from '../SavedPaymentMethods'
import WalletPaymentMethod from '../WalletPaymentMethod'

interface PurchaseMethodsProps {
  isPackPurchase: boolean
  isTradePurchase: boolean
  isPurchaseOfferPurchase: boolean
  offerId?: number
  dropId?: number
  listingId?: number
  loading: boolean
  isPaymentState: boolean
  projectId: string
  purchaseOfferId?: string
  paymentIntentData?: CollectibleProductResponse
  changeAddressCheckoutState: () => void
}

const PurchaseMethods: React.FC<PurchaseMethodsProps> = ({
  isPackPurchase,
  isTradePurchase,
  isPurchaseOfferPurchase,
  offerId,
  dropId,
  listingId,
  loading,
  isPaymentState,
  paymentIntentData,
  changeAddressCheckoutState,
  projectId,
}) => {
  const { t } = useTranslate('account')
  const { locale } = useLocale()

  const [isUseSavedPaymentMethod, setIsUseSavedPaymentMethod] = useState(
    !!paymentIntentData?.node?.paymentMethods?.length,
  )
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentIntentData?.node?.defaultPaymentMethodId || paymentIntentData?.node?.paymentMethods?.[0]?.id || '',
  )

  const handlePaymentMethodSelection = (paymentMethodId: string) => {
    setSelectedPaymentMethod(paymentMethodId)
  }

  const {
    data: paymentMethodsData,
    loading: paymentMethodsLoading,
    refetch: refetchCustomerPaymentMethods,
  } = useQuery(CUSTOMER_PAYMENT_METHODS, {
    onCompleted: (data) => {
      if (data?.customerPaymentMethods?.paymentMethods?.length === 0) {
        setIsUseSavedPaymentMethod(false)
      }
    },
    errorPolicy: 'all',
  })

  const options = {
    clientSecret: paymentIntentData?.node?.piid,
    appearance: {
      theme: 'flat',
    },
  } as StripeElementsOptions

  const paymentMethods = paymentMethodsData?.customerPaymentMethods?.paymentMethods || []

  return (
    <div className="flex flex-col">
      {!loading && !paymentIntentData?.node?.isCreditOnly && !isUseSavedPaymentMethod && (
        <div key="PaymentForm">
          <Elements options={options} stripe={getStripe(locale)}>
            <NewCardPaymentForm
              paymentIntentId={paymentIntentData?.node?.spiid || ''}
              isPaymentState={isPaymentState}
              hasPaymentMethods={!!paymentMethods.length}
              changeToSavedPaymentMethods={() => setIsUseSavedPaymentMethod(true)}
              handlePaymentMethodSelection={handlePaymentMethodSelection}
              paymentIntentData={paymentIntentData}
              projectId={projectId}
            />
          </Elements>
        </div>
      )}
      {!loading && !paymentIntentData?.node?.isCreditOnly && isUseSavedPaymentMethod && (
        <div key="PaymentForm">
          <Elements options={options} stripe={getStripe(locale)}>
            <SavedPaymentMethods
              paymentMethodsLoading={paymentMethodsLoading}
              paymentMethods={paymentMethods}
              defaultPaymentMethodId={paymentMethodsData?.customerPaymentMethods?.defaultPaymentMethodId}
              selectedPaymentMethod={selectedPaymentMethod}
              clientSecret={paymentIntentData?.node?.piid}
              paymentIntentId={paymentIntentData?.node?.spiid}
              refetchCustomerPaymentMethods={refetchCustomerPaymentMethods}
              useNewPaymentMethod={() => setIsUseSavedPaymentMethod(false)}
              handlePaymentMethodSelection={handlePaymentMethodSelection}
              paymentIntentData={paymentIntentData}
              projectId={projectId}
            />
          </Elements>
        </div>
      )}
      {paymentIntentData?.node?.isCreditOnly && (
        <div className="flex flex-col justify-center items-center">
          <WalletPaymentMethod
            isPackPurchase={isPackPurchase}
            isTradePurchase={isTradePurchase}
            isPurchaseOfferPurchase={isPurchaseOfferPurchase}
            offerId={offerId}
            dropId={dropId}
            listingId={listingId}
            isPaymentState={isPaymentState}
            changeAddressCheckoutState={changeAddressCheckoutState}
            orderId={paymentIntentData?.node?.order?.id}
            projectId={projectId}
          />
        </div>
      )}
    </div>
  )
}

export default PurchaseMethods
